import { defineStore } from 'pinia'
import { ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { api } from 'boot/receiver/axios'
import { Notify } from 'quasar'
import { $t } from 'boot/common/i18n'

import { useEventsStore } from 'stores/common/events'
import { useErrorStore } from 'stores/common/error'

import type { SuccessApiRes } from 'types/common/api'
import type { ImportStatusType } from 'types/receiver/common/bulkImport'

export const useBulkImportStore = defineStore('bulkImport', () => {
  const fetchBulkMetadata = async (
    type: 'column' | 'constraint' | 'value' | 'customer',
    successUrl: string,
    errorUrl?: string
  ) => {
    try {
      const res = await api.get(`/receive/${type}/csv/bulk/metadata`, {
        params: {
          success_url: successUrl,
          error_url: errorUrl,
        },
      })

      const data: SuccessApiRes<{ embed_url: string }> = res.data

      if (data.success) {
        return data.payload
      }
      return data.success
    } catch (error) {
      useErrorStore().updateBackErrors(error)
      return false
    }
  }

  // IMPORT STATUS
  const importStatus = ref<ImportStatusType | null>(null)

  const fetchImportStatus = async (importId: string) => {
    try {
      const res = await api.get(`/receive/resource/import/${importId}/status`)
      const data: SuccessApiRes<ImportStatusType> = res.data

      if (data.success) {
        return data.payload
      }

      return data.success
    } catch (error) {
      useErrorStore().updateBackErrors(error)
      return false
    }
  }

  const initImportWatcher = () => {
    const router = useRouter()
    const route = useRoute()
    const eventsStore = useEventsStore()

    watch(
      () => eventsStore.eventData,
      (val) => {
        if (
          !val ||
          val.type !== 'import_completed' ||
          (route.name === 'bulk-import-in-progress' &&
            route.params.importId === val.context.import_id)
        )
          return

        const isInColumnWorkflow =
          val.context.type === 'column' &&
          (route.name === 'manage-template-columns' ||
            route.name === 'import-template-columns-report')

        const isInConstraintWorkflow =
          val.context.type === 'constraint' &&
          (route.name === 'manage-template-columns' ||
            route.name === 'import-constraints-report')

        const isInValueListWorkflow =
          val.context.type === 'value_list' &&
          (route.name === 'manage-values-lists' ||
            route.name === 'import-values-lists-report')

        const isInCustomerWorkflow =
          val.context.type === 'customer' &&
          (route.name === 'manage-customers' ||
            route.name === 'import-customers-report')

        if (
          isInColumnWorkflow ||
          isInConstraintWorkflow ||
          isInValueListWorkflow ||
          isInCustomerWorkflow
        )
          return

        const type = val.context.type
        Notify.create({
          message: $t('receiver.common.bulkImport.importCompleted', {
            type:
              type === 'column'
                ? $t('common.column', 2)
                : type === 'constraint'
                  ? $t('common.constraint', 2)
                  : type === 'value_list'
                    ? $t('common.valueList', 2)
                    : $t('common.customer', 2),
          }),
          color: 'info',
          timeout: 0,
          actions: [
            {
              label: $t('common.report'),
              color: 'white',
              outline: true,
              flat: false,
              size: '12px',
              handler: async () => {
                await router.push({
                  name: 'bulk-import-in-progress',
                  params: { importId: val.context.import_id },
                })
              },
            },
          ],
        })
      }
    )
  }

  // STORE RESET
  // Used to reset the store (is given by default by pinia on options api stores but not on composition api stores)
  const $reset = () => {
    importStatus.value = null
  }

  return {
    fetchBulkMetadata,
    importStatus,
    fetchImportStatus,
    initImportWatcher,
    $reset,
  }
})
